import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { Icon } from "@/components/ui/Icon";
import { SubmitForm } from "@/components/submit/SubmitForm";
import { IdeaThankYou } from "@/components/submit/IdeaThankYou";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useResolvedWorkspaceColor } from "@/hooks/useResolvedWorkspaceColor";
import { t } from "@/lib/strings";
import type { BoardDetail, UUID } from "@/lib/types";

export function SubmitPage() {
  const { boardId } = useParams({ strict: false }) as { boardId: string };
  const navigate = useNavigate();
  const isDesktop = useBreakpoint("md");
  const { user } = useAuth();

  // submit-info is the permissive endpoint (submit_enabled, no membership) and
  // already carries the board's columns — so ANY authenticated LogicLine user
  // can render + file the form for another department's board.
  const infoQuery = useQuery({
    queryKey: ["board-submit-info", boardId],
    queryFn: () => api.getSubmitInfo(boardId as UUID),
    retry: false,
  });

  if (infoQuery.isLoading) return <CenteredCard>{t.loading}</CenteredCard>;

  if (infoQuery.error) {
    const status = infoQuery.error instanceof ApiError ? infoQuery.error.status : 0;
    if (status === 403) {
      return (
        <CenteredCard>
          <ErrorState title={t.submitDisabledTitle} body={t.submitDisabledBody} />
        </CenteredCard>
      );
    }
    return (
      <CenteredCard>
        <ErrorState
          title={t.submitDisabledTitle}
          body={infoQuery.error instanceof Error ? infoQuery.error.message : t.unknownError}
        />
      </CenteredCard>
    );
  }

  return (
    <SubmitPageCard
      detail={infoQuery.data!}
      currentUserSub={user?.sub ?? null}
      isDesktop={isDesktop}
      onSubmitted={() => navigate({ to: "/submit/$boardId/danke", params: { boardId } })}
    />
  );
}

function SubmitPageCard({
  detail,
  currentUserSub,
  isDesktop,
  onSubmitted,
}: {
  detail: BoardDetail;
  currentUserSub: string | null;
  isDesktop: boolean;
  onSubmitted: () => void;
}) {
  const tint = useResolvedWorkspaceColor(detail.workspace_id);
  return (
    <div
      style={{
        minHeight: "100%",
        background: "var(--bg)",
        padding: isDesktop ? "40px 24px 80px" : "16px 12px 80px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 640,
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          borderRadius: 12,
          padding: isDesktop ? "28px 32px 24px" : "20px 18px 20px",
          boxShadow: "0 1px 0 rgba(13,62,99,0.02)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 8,
              background: tint,
              color: "#fff",
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {detail.name[0]?.toUpperCase() ?? "?"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                color: "var(--ink-1)",
                letterSpacing: "-0.01em",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {detail.name}
            </h1>
            <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--ink-3)" }}>
              {t.submitTitle}
            </p>
          </div>
        </div>
        <p style={{ fontSize: 13, color: "var(--ink-3)", margin: "0 0 18px" }}>
          {t.submitDescription}
        </p>

        <SubmitForm
          boardId={detail.id}
          detail={detail}
          currentUserSub={currentUserSub}
          onSubmitted={onSubmitted}
        />
      </div>
    </div>
  );
}

export function SubmitDankePage() {
  const { boardId } = useParams({ strict: false }) as { boardId: string };
  const isDesktop = useBreakpoint("md");
  // Reuse the cached submit-info to learn the board kind — an ideas board gets
  // the confetti celebration; every other board keeps the plain thank-you.
  const infoQuery = useQuery({
    queryKey: ["board-submit-info", boardId],
    queryFn: () => api.getSubmitInfo(boardId as UUID),
    retry: false,
  });
  const isIdea = infoQuery.data?.kind === "ideas";

  return (
    <div
      style={{
        minHeight: "100%",
        background: "var(--bg)",
        padding: isDesktop ? "60px 24px" : "32px 16px",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 480,
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          borderRadius: 12,
          padding: "32px 28px",
          textAlign: "center",
        }}
      >
        {isIdea ? (
          <IdeaThankYou>
            <Link to="/submit/$boardId" params={{ boardId }} style={{ textDecoration: "none" }}>
              <Button variant="secondary" size="md" icon="plus">
                {t.ideaThanksAnother}
              </Button>
            </Link>
          </IdeaThankYou>
        ) : (
          <>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 999,
                background: "rgba(94,139,95,0.16)",
                color: "#3F8F65",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 14,
              }}
            >
              <Icon name="check" size={28} stroke={2.5} color="#3F8F65" />
            </div>
            <h1
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 600,
                color: "var(--ink-1)",
                letterSpacing: "-0.01em",
              }}
            >
              {t.submitDanke}
            </h1>
            <p
              style={{
                margin: "10px 0 22px",
                fontSize: 14,
                color: "var(--ink-3)",
                lineHeight: 1.5,
              }}
            >
              {t.submitDankeBody}
            </p>
            <Link to="/submit/$boardId" params={{ boardId }} style={{ textDecoration: "none" }}>
              <Button variant="secondary" size="md" icon="plus">
                {t.submitDankeAnother}
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100%",
        background: "var(--bg)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 32,
      }}
    >
      <div
        style={{
          padding: 32,
          background: "var(--surface)",
          border: "1px solid var(--hairline)",
          borderRadius: 12,
          maxWidth: 440,
          width: "100%",
          color: "var(--ink-3)",
          textAlign: "center",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ErrorState({ title, body }: { title: string; body: string }) {
  return (
    <>
      <h1
        style={{
          margin: 0,
          fontSize: 18,
          fontWeight: 600,
          color: "var(--ink-1)",
          letterSpacing: "-0.01em",
        }}
      >
        {title}
      </h1>
      <p style={{ margin: "10px 0 18px", fontSize: 13.5, color: "var(--ink-3)", lineHeight: 1.5 }}>
        {body}
      </p>
      <Link to="/" style={{ textDecoration: "none" }}>
        <Button variant="primary" size="md" icon="chevRight">
          {t.backToDashboard}
        </Button>
      </Link>
    </>
  );
}
